import { SocketMessagesApiModel } from '@api_patient/models/SocketMessagesApiModel';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { AnalyticLink, AnalyticProvider } from '@application/analytic/analytic_link/mod';
import { withTranslation } from '@application/i18n/with_translation';
import { DateDisplayFormats, DateService } from '@application/time/date.service';
import { BreakPoints, DialogAlert, Text, useBreakpoint, VisuallyHidden } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { Screen } from '@component/screen/screen.component';
import { NavBarAction, ViewNavBar } from '@component/view_nav_bar.component';
import type { MessagingMetaData } from '@feature/cases/case_messaging_model';
import { CaseService, MessagingCaseModel, MessagingData } from '@feature/cases/mod';
import { ChatCommunicatorProtocol, ParsedMessage } from '@feature/chat/chat_communicator_protocol';
import { Chat } from '@feature/chat/mod';
import type { SocketController } from '@visiba/axon';
import { useService } from '@visiba/instantiation';
import { usePresentation } from '@visiba/presentation';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

export class VisitsChatCommunicator implements ChatCommunicatorProtocol {
	public readonly analyticCategory = AnalyticCategories.Visits;

	public readonly topics = Object.freeze({
		getHistory: 'getmessages',
		retrieveMessages: 'messages',
		sendMessage: 'sendmessage',
		caseClosed: 'case_closed_v2',
		peerHasReadUntil: 'peer_has_read_until',
	});

	public readonly fileUploadDispositionUri = 'client/v2/userfiles';

	// Set to null to initialize socket instead.
	public readonly controllerInstance = null;

	constructor(public readonly connectionId: string) {
		// Empty
	}

	isValidSession(response: SocketMessagesApiModel): boolean {
		return this.connectionId === response.caseId.toString();
	}

	getHistory(controllerInstance: SocketController, callback: (data: unknown) => void): void {
		controllerInstance.invoke(
			this.topics.getHistory,
			{
				caseId: this.connectionId,
			},
			(response: SocketMessagesApiModel) => {
				callback(response);
			},
		);
	}

	parseIncomingMessages(response: SocketMessagesApiModel): ParsedMessage[] {
		return response.data;
	}

	parseOutgoingMessage(message: string | null, fileTickets: string[]): Record<string, unknown> {
		return {
			caseId: this.connectionId,
			text: message ?? '',
			fileTickets: fileTickets,
			textFormat: 'Plaintext',
		};
	}
}

interface Props {
	model: MessagingCaseModel;
}

const elements = {
	container: styled.div`
		background-color: ${tokens.color.bg.surface};
		width: 100%;
		height: 100%;
		overflow-x: hidden;
		position: relative;
	`,
	navBarWrapper: styled.div`
		position: fixed;
		top: 0;
		width: 100%;
		z-index: 1;

		${tokens.breakpointQuery.medium} {
			position: static;
		}
	`,
	hiddenNavBarGoBackLink: styled(AnalyticLink)`
		position: absolute;
		top: ${tokens.spacing.x1};
		left: ${tokens.spacing.x1};
		opacity: 0;
		z-index: -999;

		&:focus {
			opacity: 1;
			z-index: 0;
		}
	`,
};

export function InfoViewMessaging({ model }: Props): JSX.Element {
	const shouldUseScreen = useBreakpoint(BreakPoints.Small);

	return (
		<AnalyticProvider category={AnalyticCategories.VisitsChat}>
			<Screen route='infoViewMessaging' canBeActivated={shouldUseScreen} isActivated>
				<Inner model={model} />
			</Screen>
		</AnalyticProvider>
	);
}

function Inner({ model }: Props): JSX.Element {
	const data = model.get();

	const chatCommunicatorRef = useRef<VisitsChatCommunicator>();
	if (chatCommunicatorRef.current == null || chatCommunicatorRef.current.connectionId !== data.id) {
		chatCommunicatorRef.current = new VisitsChatCommunicator(data.id);
	}

	const ref = useRef<HTMLDivElement>(null);

	const presentation = usePresentation(model.presentation);
	const caseService = useService(CaseService);
	const dateService = useService(DateService);
	const analyticService = useService(AnalyticService);

	useEffect(
		function analyticQuirk_whenComponentMounts() {
			analyticService.trackPageView({ customTitle: 'My Cases-Messaging' });
		},
		[analyticService],
	);

	useEffect(
		function whenComponentMounts() {
			model.analyticQuirk_trackOnMountInVisitsView();
			model.updateExtendedData();
		},
		[model],
	);

	function handleMarkAsCompleted(): void {
		model.markAsCompleted(dateService.newDate());
	}

	function handleReceivedHistory(): void {
		model.markAsRead();
		caseService.updateSync();
	}

	async function handleCloseCase(): Promise<void> {
		return await model.closeCase();
	}

	const renderBottomBanner = () => {
		if (presentation.metaData?.completedTimestamp != null) {
			return (
				<Text variant='labelXSmallRegular' style={{ color: tokens.color.content.muted }}>
					{withTranslation('PW.Component.Visits.MessagingInfo.CaseCompleted', {
						dateTime: presentation.metaData.completedTimestamp.format(
							dateService.getLocaleSpecificDateFormatTemplate(DateDisplayFormats.FULL_DISPLAY_IN_CURRENT_YEAR_NO_WEEKDAY),
						),
					})}
				</Text>
			);
		}
		if (!presentation.metaData?.allowClientResponse) {
			return (
				<Text variant='labelXSmallRegular' style={{ color: tokens.color.content.muted }}>
					{withTranslation('PW.Component.Visits.MessagingInfo.ResponseNotAllowed')}
				</Text>
			);
		}
		return null;
	};

	return (
		<elements.container role='region' aria-labelledby='infoViewMessageTitle' ref={ref}>
			<VisuallyHidden key={data.id /* Key here to get React to invalidate the v-dom diff. Forcing a rerender of this component */}>
				<h1 id='infoViewMessageTitle'>{withTranslation('PW.Component.Visits.MessagingInfo.A11y.Region')}</h1>
			</VisuallyHidden>
			<Chat
				communicator={chatCommunicatorRef.current}
				issuesTrackedById={MessagingCaseModel.MESSAGE_UNHANDLED_ISSUES_TRACKING_ID_PREFIX + data.id}
				onMarkAsCompleted={handleMarkAsCompleted}
				onReceivedHistory={handleReceivedHistory}
				connectedId={data.id}
				forceHideActions={data.completed || !presentation.metaData?.allowClientResponse || !!presentation.metaData.completedTimestamp}
				messagingMetaData={presentation.metaData}
				header={<NavBar data={data} metaData={presentation.metaData} onCloseCase={handleCloseCase} />}
				showTopBanner={
					<Text variant='labelXSmallRegular' style={{ color: tokens.color.content.muted }}>
						{withTranslation('PW.Component.Visits.MessagingInfo.CaseStarted', {
							dateTime: data.date.format(
								DateService.getLocaleSpecificDateFormatTemplate(DateDisplayFormats.FULL_DISPLAY_IN_CURRENT_YEAR_NO_WEEKDAY),
							),
						})}
					</Text>
				}
				showBottomBanner={renderBottomBanner()}
			/>
		</elements.container>
	);
}

interface NavBarProps {
	data: MessagingData;
	metaData: MessagingMetaData | null;
	onCloseCase: () => Promise<void>;
}
function NavBar({ data, metaData, onCloseCase }: NavBarProps): JSX.Element {
	const desktop = useBreakpoint(BreakPoints.Medium);
	const analyticAction = 'Clicked go to My cases-back arrow';
	const [showConfirmCloseModal, setShowConfirmCloseModal] = useState(false);

	const receiptUrl = metaData?.receiptUrl;
	const actions: NavBarAction[] = [];

	if (receiptUrl) {
		actions.push({
			label: withTranslation('PW.Component.Visits.MessagingInfo.ShowReceipt'),
			onSelect: () => window.open(receiptUrl, '_blank'),
		});
	}

	if (metaData?.completedTimestamp === undefined && metaData?.allowClientToComplete) {
		actions.push({ label: withTranslation('Messaging.CloseCase.Menu.Item'), onSelect: () => setShowConfirmCloseModal(true) });
	}

	return (
		<elements.navBarWrapper>
			<DialogAlert
				header={withTranslation('Messaging.CloseCase.Dialog.Title')}
				description={withTranslation('Messaging.CloseCase.Dialog.Text')}
				abortText={withTranslation('Messaging.CloseCase.Dialog.Cancel.Button')}
				successText={withTranslation('Messaging.CloseCase.Dialog.Ok.Button')}
				isOpen={showConfirmCloseModal}
				onSuccess={async () => {
					await onCloseCase();
					setShowConfirmCloseModal(false);
				}}
				onAbort={() => setShowConfirmCloseModal(false)}
			/>
			{desktop ? (
				<elements.hiddenNavBarGoBackLink action={analyticAction} to='../../' replace>
					{withTranslation('PW.Component.Visits.MessagingInfo.A11y.GoBack')}
				</elements.hiddenNavBarGoBackLink>
			) : null}
			<ViewNavBar analyticAction={analyticAction} to={!desktop ? '../../' : undefined} title={data.receptionName} actions={actions} />
		</elements.navBarWrapper>
	);
}
